/*
 * HappyAlertHost — the single premium alert renderer for the whole app.
 * ------------------------------------------------------------------
 * Mounted once in AppProviders. Subscribes to HappyAlertController and renders
 * a SOLID, theme-aware, Lottie-animated success / error / confirm modal.
 *
 * Visual style ("Happy Premium"): solid white / navy card, radius 28, soft
 * brand-orange glow behind the icon, spring entrance, pill buttons (brand-orange
 * primary with white text, calm grey secondary), Lottie checkmark/cross for
 * success/error, vector icon for warning/info. Never frosted or translucent.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Animated,
    Easing,
    Modal,
    Pressable,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
    useWindowDimensions,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import LottieView from 'lottie-react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import { BRAND, useManagementTheme, withAlpha } from '../../helpers/managementTheme';
import HappyAlertController from './controller';

// Branded Lottie animations: green check + confetti for success, an orange "!"
// badge for everything that needs attention (error / warning / action required).
// The badge's layers are named `badge` / `pulse` / `shadow`, so the host re-tints
// them per alert type (red for errors, amber for warnings, brand orange otherwise).
const SUCCESS_LOTTIE = require('../../../assets/animations/happy_success.json');
const ALERT_LOTTIE = require('../../../assets/animations/happy_alert.json');
// Branded "leave page? / unsaved changes" mark — plays once and freezes.
const CONFIRM_EXIT_LOTTIE = require('../../../assets/animations/confirm_exit.json');

// `color` is a management-theme tone key (see `useManagementTheme().toneColor`).
const TYPE_META = {
    success: { lottie: SUCCESS_LOTTIE, color: 'success', icon: 'check-circle' },
    error: { lottie: ALERT_LOTTIE, color: 'danger', icon: 'close-circle', tint: true },
    warning: { lottie: ALERT_LOTTIE, color: 'warning', icon: 'alert', tint: true },
    info: { lottie: null, color: 'info', icon: 'information' },
    question: { lottie: ALERT_LOTTIE, color: 'primary', icon: 'help-circle', tint: true },
    confirmExit: { lottie: CONFIRM_EXIT_LOTTIE, color: 'warning', icon: 'exit-to-app' },
};

/**
 * The single premium alert renderer (mounted once in AppProviders). Renders the
 * queued {@link HappyAlertController} configs as a solid, rounded, themed modal.
 *
 * @returns {?JSX.Element} The modal while an alert is active, otherwise `null`.
 */
const HappyAlertHost = () => {
    const m = useManagementTheme();
    const { isDark } = m;
    const { width } = useWindowDimensions();

    const [current, setCurrent] = useState(null);
    const [visible, setVisible] = useState(false);
    const queueRef = useRef([]);
    // Guards the close sequence so it runs exactly once even if BOTH the
    // animation-completion callback and the watchdog timer fire (or neither
    // does and we force it). See `close()` for why the watchdog exists.
    const closingRef = useRef(false);
    const closeWatchdogRef = useRef(null);

    const backdrop = useRef(new Animated.Value(0)).current;
    const scale = useRef(new Animated.Value(0.85)).current;
    const translate = useRef(new Animated.Value(18)).current;

    const animateIn = useCallback(() => {
        backdrop.setValue(0);
        scale.setValue(0.85);
        translate.setValue(18);
        Animated.parallel([
            Animated.timing(backdrop, { toValue: 1, duration: 200, useNativeDriver: true }),
            Animated.spring(scale, { toValue: 1, friction: 7, tension: 70, useNativeDriver: true }),
            Animated.spring(translate, { toValue: 0, friction: 8, tension: 70, useNativeDriver: true }),
        ]).start();
    }, [backdrop, scale, translate]);

    const showNext = useCallback(() => {
        const next = queueRef.current.shift();
        if (!next) return;
        setCurrent(next);
        setVisible(true);
        requestAnimationFrame(animateIn);
    }, [animateIn]);

    const finishClose = useCallback(() => {
        if (closeWatchdogRef.current) {
            globalThis.clearTimeout(closeWatchdogRef.current);
            closeWatchdogRef.current = null;
        }
        closingRef.current = false;
        setVisible(false);
        setCurrent(null);
        if (queueRef.current.length) {
            requestAnimationFrame(showNext);
        }
    }, [showNext]);

    const close = useCallback(() => {
        // Ignore re-entrant close() calls (e.g. backdrop tap + button press).
        if (closingRef.current) return;
        closingRef.current = true;

        // CRITICAL: never let the modal's teardown depend solely on the
        // Animated completion callback. A native OS dialog opened from a
        // button's onPress — the notification-permission prompt fired right
        // after tapping "Allow" is the canonical case — steals focus and
        // interrupts the native-driver animation, so `.start(cb)` may never
        // fire. Without this watchdog the <Modal> and its full-screen
        // touch-capturing backdrop would stay mounted forever and freeze the
        // entire app (no menu / no button responds). The timer force-tears the
        // modal down after the animation's worst-case duration no matter what.
        let settled = false;
        const settle = () => {
            if (settled) return;
            settled = true;
            finishClose();
        };

        closeWatchdogRef.current = globalThis.setTimeout(settle, 400);

        Animated.parallel([
            Animated.timing(backdrop, { toValue: 0, duration: 160, useNativeDriver: true }),
            Animated.timing(scale, { toValue: 0.9, duration: 160, easing: Easing.in(Easing.ease), useNativeDriver: true }),
            Animated.timing(translate, { toValue: 10, duration: 160, useNativeDriver: true }),
        ]).start(settle);
    }, [backdrop, scale, translate, finishClose]);

    // Subscribe to the controller.
    useEffect(() => {
        const unsubscribe = HappyAlertController.subscribe((config) => {
            if (config && config.__dismiss) {
                close();
                return;
            }
            queueRef.current.push(config);
            if (!visible) showNext();
        });
        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, showNext, close]);

    // Drop the close watchdog if the host unmounts mid-close.
    useEffect(() => () => {
        if (closeWatchdogRef.current) {
            globalThis.clearTimeout(closeWatchdogRef.current);
            closeWatchdogRef.current = null;
        }
    }, []);

    const handleButtonPress = useCallback(
        (button) => {
            close();
            // Fire the action while the close animation plays so navigation feels snappy.
            if (button && typeof button.onPress === 'function') button.onPress();
        },
        [close],
    );

    const handleBackdrop = useCallback(() => {
        if (current && current.cancelable === false) return;
        if (current && typeof current.onDismiss === 'function') current.onDismiss();
        close();
    }, [current, close]);

    if (!current) return null;

    const meta = TYPE_META[current.type] || TYPE_META.info;
    const accent = m.toneColor(meta.color);
    const brand = BRAND.base; // brand orange
    const cardBg = m.colors.surface; // solid — never translucent
    const titleColor = m.colors.heading;
    const subColor = m.colors.subText;

    const buttons = Array.isArray(current.buttons) && current.buttons.length
        ? current.buttons
        : [{ text: 'OK' }];
    const stacked = buttons.length > 2;
    const cardWidth = Math.min(width - 48, 380);

    const renderButton = (button, index) => {
        const isCancel = button.style === 'cancel';
        const isDestructive = button.style === 'destructive';
        const isPrimary = !isCancel; // confirm / default / destructive are emphasised
        const key = `${button.text || 'btn'}-${index}`;

        if (isPrimary) {
            // Solid pill: brand orange (destructive → semantic red), white text.
            const fill = isDestructive ? m.colors.danger : brand;
            return (
                <TouchableOpacity
                    key={key}
                    activeOpacity={0.85}
                    style={[styles.btnWrap, stacked && styles.btnWrapStacked, { backgroundColor: fill }]}
                    onPress={() => handleButtonPress(button)}
                >
                    <View style={styles.btnPrimary}>
                        <Text style={styles.btnPrimaryText}>{button.text}</Text>
                    </View>
                </TouchableOpacity>
            );
        }

        return (
            <TouchableOpacity
                key={key}
                activeOpacity={0.7}
                style={[
                    styles.btnWrap,
                    stacked && styles.btnWrapStacked,
                    styles.btnSecondary,
                    { backgroundColor: m.colors.surfaceAlt },
                ]}
                onPress={() => handleButtonPress(button)}
            >
                <Text style={[styles.btnSecondaryText, { color: m.colors.text }]}>{button.text}</Text>
            </TouchableOpacity>
        );
    };

    return (
        <Modal transparent visible={visible} animationType="none" onRequestClose={handleBackdrop} statusBarTranslucent>
            <Animated.View style={[styles.backdrop, { opacity: backdrop }]}>
                <Pressable style={StyleSheet.absoluteFill} onPress={handleBackdrop} />
                <Animated.View
                    style={[
                        styles.card,
                        {
                            width: cardWidth,
                            backgroundColor: cardBg,
                            borderColor: m.colors.border,
                            opacity: backdrop,
                            transform: [{ scale }, { translateY: translate }],
                        },
                    ]}
                >
                    {/* Soft brand-orange glow behind the icon */}
                    <LinearGradient
                        colors={[withAlpha(brand, isDark ? 0.22 : 0.16), 'rgba(0,0,0,0)']}
                        start={{ x: 0.5, y: 0 }}
                        end={{ x: 0.5, y: 1 }}
                        style={styles.topGlow}
                        pointerEvents="none"
                    />

                    {/* Big animated icon */}
                    <Animated.View
                        style={[
                            styles.iconHalo,
                            {
                                backgroundColor: withAlpha(accent, isDark ? 0.2 : 0.13),
                                transform: [{ scale }],
                            },
                        ]}
                    >
                        {meta.lottie ? (
                            <LottieView
                                source={meta.lottie}
                                autoPlay
                                loop={false}
                                style={styles.lottie}
                                colorFilters={meta.tint ? ['badge', 'pulse', 'shadow'].map((keypath) => ({ keypath, color: accent })) : undefined}
                            />
                        ) : (
                            <MaterialCommunityIcons name={meta.icon} size={74} color={accent} />
                        )}
                    </Animated.View>

                    {!!current.title && (
                        <Text style={[styles.title, { color: titleColor }]}>{current.title}</Text>
                    )}
                    {!!current.message && (
                        <Text style={[styles.message, { color: subColor }]}>{current.message}</Text>
                    )}

                    <View style={[styles.actions, stacked ? styles.actionsStacked : styles.actionsRow]}>
                        {buttons.map(renderButton)}
                    </View>
                </Animated.View>
            </Animated.View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: 'rgba(15, 23, 42, 0.55)',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
    },
    card: {
        borderRadius: 28,
        borderWidth: 1,
        paddingTop: 30,
        paddingBottom: 22,
        paddingHorizontal: 24,
        alignItems: 'center',
        overflow: 'hidden',
        shadowColor: '#0b1020',
        shadowOffset: { width: 0, height: 18 },
        shadowOpacity: 0.28,
        shadowRadius: 30,
        elevation: 16,
    },
    topGlow: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 170,
    },
    iconHalo: {
        width: 132,
        height: 132,
        borderRadius: 66,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
    },
    lottie: {
        width: 132,
        height: 132,
    },
    title: {
        fontSize: 21,
        fontWeight: '800',
        textAlign: 'center',
        letterSpacing: -0.3,
        marginBottom: 6,
    },
    message: {
        fontSize: 15,
        lineHeight: 21,
        textAlign: 'center',
        marginBottom: 4,
    },
    actions: {
        width: '100%',
        marginTop: 22,
    },
    actionsRow: {
        flexDirection: 'row',
        gap: 10,
    },
    actionsStacked: {
        flexDirection: 'column',
        gap: 10,
    },
    btnWrap: {
        flex: 1,
        borderRadius: 999,
        overflow: 'hidden',
    },
    btnWrapStacked: {
        flex: 0,
        width: '100%',
    },
    btnPrimary: {
        paddingVertical: 14,
        alignItems: 'center',
        justifyContent: 'center',
    },
    btnPrimaryText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '800',
    },
    btnSecondary: {
        paddingVertical: 14,
        alignItems: 'center',
        justifyContent: 'center',
    },
    btnSecondaryText: {
        fontSize: 16,
        fontWeight: '700',
    },
});

export default HappyAlertHost;
